import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { LabResult } from '../../../services/lab-results-service/types';
import { useLabResultsStore } from '../../../store/lab-results-store';

interface Props {
  id: string | undefined;
  children: React.ReactNode;
}

interface LabResultContextState {
  labResult: LabResult;
  error: string;
}

const emptyState: LabResult = {
  id: 0,
  accession: '',
  firstName: '',
  lastName: '',
  email: '',
  originalOrderDate: '',
  resultDetails: [],
};

const LabResultContext = createContext<LabResultContextState>({
  labResult: emptyState,
  error: '',
});

export const useLabResultContext = (): LabResultContextState => {
  const context = useContext(LabResultContext);
  if (!context) {
    throw new Error(
      'useLabResultContext must be used inside a LabResultProvider',
    );
  }
  return context;
};

export const LabResultProvider: React.FC<Props> = ({ id, children }: Props) => {
  const getLabResultById = useLabResultsStore(state => state.getLabResultById);
  const clearCurrentLabResult = useLabResultsStore(
    state => state.clearCurrentLabResult,
  );
  const labResult = useLabResultsStore(state => state.currentLabResult);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let parsedId: number;
    try {
      parsedId = Number(id);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setError(`Invalid ID: ${id}`);
      return () => {};
    }
    setError('');
    getLabResultById(parsedId);
    return () => {
      clearCurrentLabResult();
    };
  }, [clearCurrentLabResult, getLabResultById, id, setError]);

  // memoized to avoid react/jsx-no-constructed-context-values
  const values = useMemo<LabResultContextState>(
    () => ({
      labResult: labResult || emptyState,
      error,
    }),
    [labResult, error],
  );

  return (
    <LabResultContext.Provider value={values}>
      {error && <p className="py-2">{error}</p>}
      {children}
    </LabResultContext.Provider>
  );
};
